/**
 * The right column composing a new session — the A2 canon's "Nowa sesja": who, what, when and how
 * long, what the hour runs into, and "Zaplanuj sesję".
 *
 * The draft is `useSessionWrites`' — the grid draws it too and a drag there changes it here — so
 * this component holds only the one thing a keyboard needs held: a length field emptied on the way
 * to a new number, which the draft cannot hold.
 *
 * Three notes stand under the fields, each for its own reason and never in colour alone:
 * - "Poza grafikiem" — allowed; a client could not have taken this hour themselves;
 * - the sessions the hour overlaps — allowed or not is the server's to say (see `model/busyTime`);
 * - the server's refusal, in its own words and in red — the one that stopped the write.
 * A draft that cannot be sent yet keeps "Zaplanuj sesję" focusable and says why beside it; the
 * field in the way is marked (`aria-invalid`, its reason under it), and pressing the primary anyway
 * takes focus there. The browser's own validation is off — one voice, not two.
 *
 * The foot — the refusal, the reason and the primary — is sticky at the column's bottom, so at
 * 1280×620 the fields scroll above it and "Zaplanuj sesję" never falls below the fold.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ember from '~/shared/ui/emberButton.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import { FormField } from '~/shared/ui/FormField';
import { Toggle } from '~/shared/ui/Toggle';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import type { DraftState } from '../lib/useSessionWrites';
import {
  DRAFT_PROBLEM_FIELD,
  STEP_MINUTES,
  type DraftField,
  DURATION_BOUNDS,
  type DraftPolicy,
  type SessionDraft,
} from '../model/sessionDraft';
import { minutesOfTime, paddedClock } from '../model/trainerClock';
import type { SessionKind } from '../model/bookingSession';
import type { CallLinkChoiceView } from '../model/callLinkChoice';
import { CallLinkChoice } from './CallLinkChoice';
import { ClientField } from './ClientField';
import { KindChoice } from './KindChoice';
import styles from './ComposeForm.module.css';

interface NewSessionPanelProps {
  readonly composing: DraftState;
  readonly policy: DraftPolicy;
  /** The backend knows video calls; an older one would drop the choice silently. */
  readonly videoCallOffered: boolean;
  /** Where a video consultation's link comes from (`model/callLinkChoice`); `null` while Google is read. */
  readonly callChoice: CallLinkChoiceView | null;
  readonly onEdit: (patch: Partial<SessionDraft>) => void;
  readonly onKind: (kind: SessionKind) => void;
  readonly onSubmit: () => void;
  readonly onRepeat: () => void;
  readonly onClose: () => void;
}

export function NewSessionPanel({
  composing,
  policy,
  videoCallOffered,
  callChoice,
  onEdit,
  onKind,
  onSubmit,
  onRepeat,
  onClose,
}: NewSessionPanelProps) {
  const { t } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);
  const reasonId = useId();
  const [lengthCleared, setLengthCleared] = useState(false);
  const { draft, review, saving, refusal } = composing;
  const reason = review.problem === null ? null : t(`web:booking.create.problem.${review.problem}`);
  const form = useRef<HTMLFormElement>(null);
  /** The reason a field wears, when the draft's problem is about that field. */
  const errorOn = (field: DraftField) =>
    review.problem !== null && DRAFT_PROBLEM_FIELD[review.problem] === field ? reason : null;

  // The column changed subject: the reader is taken to it.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <form
      ref={form}
      className={styles.panel}
      aria-busy={saving}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (review.problem !== null) {
          // Refused: to the field the problem is about (`DRAFT_PROBLEM_FIELD`), not the first one
          // in document order.
          form.current
            ?.querySelector<HTMLElement>(
              `[data-field="${DRAFT_PROBLEM_FIELD[review.problem]}"] :is(input, button)`,
            )
            ?.focus();
          return;
        }
        onSubmit();
      }}
    >
      <div className={styles.panelHead}>
        <h2 ref={heading} tabIndex={-1} className={styles.panelTitle}>
          {t('calendar.create.titleOnce')}
        </h2>
        <button
          type="button"
          className={styles.close}
          aria-label={t('common.close')}
          aria-disabled={saving ? 'true' : undefined}
          onClick={onClose}
        >
          <CloseGlyph />
        </button>
      </div>

      <ClientField
        clientId={draft.clientId}
        reasonId={review.problem === 'noClient' ? reasonId : null}
        onChoose={(clientId) => {
          onEdit({ clientId });
        }}
      />

      <KindChoice kind={draft.kind} policy={policy} onChange={onKind} />

      {draft.kind === 'consultation' && videoCallOffered ? (
        <div className={styles.videoCall}>
          <div className={styles.videoCallRow}>
            <span className={styles.label} aria-hidden="true">
              {t('web:booking.create.videoCall')}
            </span>
            <Toggle
              checked={draft.videoCall}
              label={t('web:booking.create.videoCall')}
              onChange={(videoCall) => {
                onEdit({ videoCall });
              }}
            />
          </div>
          {draft.videoCall && callChoice !== null ? (
            <CallLinkChoice
              choice={callChoice}
              source={draft.callSource ?? callChoice.defaultSource}
              text={draft.callLinkText ?? callChoice.prefill}
              error={errorOn('callLink')}
              onSource={(callSource) => {
                onEdit({ callSource });
              }}
              onText={(callLinkText) => {
                onEdit({ callLinkText });
              }}
            />
          ) : null}
        </div>
      ) : null}

      <div className={styles.pair}>
        <div data-field="date">
          <FormField
            label={t('web:booking.create.date')}
            type="date"
            value={draft.day}
            error={errorOn('date')}
            onChange={(value) => {
              if (value !== '') onEdit({ day: value });
            }}
          />
        </div>
        <div data-field="time">
          <FormField
            label={t('web:booking.create.time')}
            type="time"
            step={STEP_MINUTES * 60}
            value={paddedClock(draft.startMinutes)}
            error={errorOn('time')}
            onChange={(value) => {
              const minutes = minutesOfTime(value);
              if (minutes !== null) onEdit({ startMinutes: minutes });
            }}
          />
        </div>
      </div>

      <div data-field="duration">
        <FormField
          label={t('calendar.create.durationLabel')}
          type="number"
          inputMode="numeric"
          step={5}
          min={String(DURATION_BOUNDS.min)}
          max={String(DURATION_BOUNDS.max)}
          value={lengthCleared ? '' : String(draft.durationMinutes)}
          error={errorOn('duration')}
          onChange={(value) => {
            setLengthCleared(value === '');
            const minutes = Number(value);
            if (value !== '' && Number.isFinite(minutes)) onEdit({ durationMinutes: minutes });
          }}
          trailing={<span className={styles.unit}>{t('web:booking.create.minutes')}</span>}
        />
      </div>

      <div className={styles.notes}>
        {review.outsideHours === true ? (
          <p className={styles.note}>
            <span className={styles.noteMark} aria-hidden="true" />
            {t('web:booking.create.outsideHours')}
          </p>
        ) : null}
        {review.overlaps.length > 0 ? (
          <p className={styles.note}>
            <WarningGlyph className={styles.noteGlyph} />
            {t('web:booking.create.overlap', {
              names: review.overlaps
                .map((session) =>
                  `${session.clientName ?? t('web:booking.unnamedClient')} ${session.timeLabel ?? ''}`.trim(),
                )
                .join(', '),
            })}
          </p>
        ) : null}
      </div>

      <div className={styles.panelFoot}>
        {refusal === null ? null : (
          <p className={styles.refusal} role="alert">
            <WarningGlyph className={styles.noteGlyph} />
            {refusal}
          </p>
        )}
        {reason === null ? null : (
          <p id={reasonId} className={styles.reason}>
            {reason}
          </p>
        )}
        <div className={styles.footRow}>
          <button
            type="button"
            className={styles.repeat}
            aria-disabled={saving ? 'true' : undefined}
            onClick={onRepeat}
          >
            {t('web:booking.create.repeat')}
          </button>
          <span className={styles.spacer} />
          <button
            type="submit"
            className={`${String(ember.ember)} ${String(styles.submit)}`}
            aria-disabled={review.problem !== null || saving ? 'true' : undefined}
            aria-describedby={reason === null ? undefined : reasonId}
          >
            {saving ? t('web:booking.create.saving') : t('calendar.create.ctaOnce')}
          </button>
        </div>
      </div>
    </form>
  );
}
